import { keepPreviousData, useMutation, useQueries, useQuery } from '@tanstack/react-query'

import { isApiError } from '@/api'
import type { SummaryFeedbackRating } from '@/api/generated/api'
import {
  createSummaryFeedbackAudioUrl,
  getSummaryFeedback,
  getSummaryFeedbackStats,
  listSummaryFeedback,
  type SummaryFeedbackStatsBucket,
} from '@/api/meetingSummaryFeedback'
import { getSummaryQualityStats } from '@/api/summaryQuality'
import { shouldRetry } from '@/pages/app/ReportedMeetings/useReportedMeetings'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** Any UUID version: the client mints the id, and `AttributionViewer/meetingId` only accepts v1–5. */
export const normalizeFeedbackId = (raw: string | undefined): string | null => {
  const id = raw?.trim().toLowerCase()
  return id && UUID_RE.test(id) ? id : null
}

/** `since` and `until` are ISO timestamps; `until` is exclusive. */
export type SummaryFeedbackFilters = {
  rating?: SummaryFeedbackRating
  promptVersion?: string
  summaryModel?: string
  since?: string
  until?: string
}

export type SummaryFeedbackStatsParams = {
  since: string
  until: string
  bucket: SummaryFeedbackStatsBucket
  promptVersion?: string
  summaryModel?: string
}

export const summaryFeedbackKeys = {
  list: ({ rating, promptVersion, summaryModel, since, until }: SummaryFeedbackFilters, cursor: string | undefined) =>
    [
      'summary-feedback',
      'list',
      rating ?? null,
      promptVersion ?? null,
      summaryModel ?? null,
      since ?? null,
      until ?? null,
      cursor ?? 'first',
    ] as const,
  detail: (feedbackId: string | null) => ['summary-feedback', feedbackId] as const,
  stats: ({ since, until, bucket, promptVersion, summaryModel }: SummaryFeedbackStatsParams) =>
    ['summary-feedback', 'stats', since, until, bucket, promptVersion ?? null, summaryModel ?? null] as const,
}

export const errorStatus = (error: unknown) => (isApiError(error) ? error.status : undefined)

// Every GET writes an audit record server-side, so the page reads once and never refreshes on its own.
export const useSummaryFeedback = (feedbackId: string | null) =>
  useQuery({
    queryKey: summaryFeedbackKeys.detail(feedbackId),
    queryFn: ({ signal }) => getSummaryFeedback(feedbackId as string, signal),
    enabled: feedbackId !== null,
    retry: shouldRetry,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

const LIST_STALE_MS = 10 * 60_000

// One page per query key, so returning to the list re-reads (and audits) only the page on screen.
export const useSummaryFeedbackList = (filters: SummaryFeedbackFilters, cursor: string | undefined, enabled = true) =>
  useQuery({
    queryKey: summaryFeedbackKeys.list(filters, cursor),
    queryFn: ({ signal }) => listSummaryFeedback({ ...filters, cursor }, signal),
    enabled,
    placeholderData: keepPreviousData,
    retry: shouldRetry,
    staleTime: LIST_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

const statsOptions = {
  placeholderData: keepPreviousData,
  retry: shouldRetry,
  staleTime: LIST_STALE_MS,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
}

const statsQuery = (params: SummaryFeedbackStatsParams) => ({
  ...statsOptions,
  queryKey: summaryFeedbackKeys.stats(params),
  queryFn: ({ signal }: { signal: AbortSignal }) => getSummaryFeedbackStats(params, signal),
})

export const useSummaryFeedbackStats = (params: SummaryFeedbackStatsParams, enabled = true) =>
  useQuery({ ...statsQuery(params), enabled })

export const useSummaryFeedbackStatsSeries = (series: SummaryFeedbackStatsParams[], enabled = true) =>
  useQueries({ queries: series.map((params) => ({ ...statsQuery(params), enabled })) })

export const summaryQualityKeys = {
  stats: ({ since, until, bucket, promptVersion, summaryModel }: SummaryFeedbackStatsParams) =>
    ['summary-quality', 'stats', since, until, bucket, promptVersion ?? null, summaryModel ?? null] as const,
}

export const useSummaryQualityStats = (params: SummaryFeedbackStatsParams, enabled = true) =>
  useQuery({
    ...statsOptions,
    queryKey: summaryQualityKeys.stats(params),
    queryFn: ({ signal }) => getSummaryQualityStats(params, signal),
    enabled,
  })

export const useSummaryFeedbackAudioUrl = (
  feedbackId: string,
  handlers: { onError: (error: unknown) => void; onSuccess: () => void }
) =>
  useMutation({
    mutationFn: () => createSummaryFeedbackAudioUrl(feedbackId),
    onError: handlers.onError,
    onSuccess: handlers.onSuccess,
  })
